/**
 * PWA install prompt banner.
 *
 * Chrome + Edge + Samsung Internet + Chrome Android fire the
 * `beforeinstallprompt` event when the site is installable but hasn't been
 * installed yet. Safari on iOS doesn't — we detect iOS separately and show
 * the "Add to Home Screen" hint.
 *
 * Dismissed state persists in localStorage so we don't nag on every page load.
 */
import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const DISMISS_KEY = "clearbooks.install.dismissed";

function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIos && isSafari;
}

function isStandalone(): boolean {
  // Chrome exposes matchMedia; iOS Safari sets navigator.standalone.
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  const nav = navigator as Navigator & { standalone?: boolean };
  return !!nav.standalone;
}

export function InstallBanner() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(DISMISS_KEY) === "1";
  });
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissed) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari never fires beforeinstallprompt — surface a hint instead
    // once we know we're on iOS Safari, not already installed.
    if (isIosSafari()) setShowIosHint(true);

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [dismissed]);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
    setPromptEvent(null);
    setShowIosHint(false);
  }

  async function install() {
    if (!promptEvent) return;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice.outcome === "accepted") dismiss();
    setPromptEvent(null);
  }

  if (dismissed) return null;
  if (isStandalone()) return null;
  if (!promptEvent && !showIosHint) return null;

  return (
    <div className="install-banner" role="region" aria-label="Install ClearBooks">
      <div className="install-banner-icon" aria-hidden="true">📲</div>
      <div className="install-banner-copy">
        <div className="install-banner-title">Install ClearBooks</div>
        <div className="install-banner-sub">
          {promptEvent
            ? "Add it to your home screen for one-tap access + camera photo uploads."
            : "Tap the Share button in Safari, then \"Add to Home Screen\"."}
        </div>
      </div>
      <div className="install-banner-actions">
        {promptEvent && (
          <button type="button" className="btn btn-primary" onClick={install}>
            Install
          </button>
        )}
        <button type="button" className="btn btn-ghost install-banner-close" onClick={dismiss} aria-label="Dismiss">
          ×
        </button>
      </div>
    </div>
  );
}
